"use client";

/**
 * Post-purchase confirmation modal.
 *
 * The prominent success experience shown after any completed upgrade or
 * purchase — replacing the old bottom-right toast. It recaps the purchased
 * plan / credit amount, lists the most relevant unlocked benefits, and offers a
 * single clear CTA to continue building.
 *
 * Rendered by <PurchaseConfirmationHost /> (mounted at the app root) whenever a
 * call site fires `showPurchaseConfirmation(...)`. Theme-aware via the popover
 * CSS tokens; the celebratory confetti is suppressed for users who prefer
 * reduced motion. Analytics for "shown" and "cta clicked" fire from here so
 * every trigger site gets consistent tracking for free.
 */
import { useEffect, useMemo } from "react";
import { useRouter } from "next/navigation";
import { Check, X } from "lucide-react";

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogTitle,
} from "@/components/ui/dialog";
import { usePrefersReducedMotion } from "@/hooks/use-prefers-reduced-motion";
import { resolvePurchaseConfirmation } from "@/lib/billing/purchase-confirmation";
import {
  trackPurchaseConfirmationCtaClicked,
  trackPurchaseConfirmationShown,
} from "@/lib/analytics";
import type { PurchaseConfirmationRequest } from "./purchase-confirmation-store";

/**
 * Deterministic confetti field — fixed pieces so the burst looks the same every
 * time (and never needs Math.random at render). Each piece carries its own
 * horizontal start, drift, colour, delay and duration via CSS variables that
 * the `confirmation-confetti` keyframe (globals.css) reads.
 */
const CONFETTI_PIECES = [
  { left: "8%", x: "-24px", color: "#22C55E", delay: 0, duration: 1500, rot: "480deg", size: 8 },
  { left: "18%", x: "16px", color: "#F59E0B", delay: 120, duration: 1700, rot: "-540deg", size: 6 },
  { left: "27%", x: "-12px", color: "#14B8A6", delay: 60, duration: 1600, rot: "600deg", size: 9 },
  { left: "36%", x: "28px", color: "#10B981", delay: 200, duration: 1800, rot: "-420deg", size: 7 },
  { left: "45%", x: "-20px", color: "#6366F1", delay: 40, duration: 1550, rot: "520deg", size: 8 },
  { left: "54%", x: "20px", color: "#84CC16", delay: 160, duration: 1750, rot: "-600deg", size: 6 },
  { left: "63%", x: "-28px", color: "#F59E0B", delay: 90, duration: 1650, rot: "440deg", size: 9 },
  { left: "72%", x: "12px", color: "#10B981", delay: 220, duration: 1700, rot: "-500deg", size: 7 },
  { left: "81%", x: "-16px", color: "#059669", delay: 30, duration: 1600, rot: "560deg", size: 8 },
  { left: "90%", x: "24px", color: "#FBBF24", delay: 140, duration: 1720, rot: "-460deg", size: 6 },
  { left: "13%", x: "18px", color: "#6366F1", delay: 260, duration: 1680, rot: "500deg", size: 7 },
  { left: "50%", x: "-8px", color: "#F59E0B", delay: 300, duration: 1620, rot: "-560deg", size: 9 },
  { left: "68%", x: "8px", color: "#22C55E", delay: 340, duration: 1740, rot: "480deg", size: 6 },
  { left: "85%", x: "-22px", color: "#10B981", delay: 280, duration: 1660, rot: "-520deg", size: 8 },
] as const;

function Confetti() {
  return (
    <div
      aria-hidden
      className="pointer-events-none absolute inset-0 z-30 overflow-hidden"
    >
      {CONFETTI_PIECES.map((p, i) => (
        <span
          key={i}
          className="animate-confirmation-confetti absolute top-0 block rounded-[2px]"
          style={{
            left: p.left,
            width: p.size,
            height: p.size * 1.6,
            backgroundColor: p.color,
            // Read by the keyframe in globals.css.
            ["--confetti-x" as string]: p.x,
            ["--confetti-rot" as string]: p.rot,
            ["--confetti-delay" as string]: `${p.delay}ms`,
            ["--confetti-duration" as string]: `${p.duration}ms`,
          }}
        />
      ))}
    </div>
  );
}

export function PurchaseConfirmationModal({
  request,
  onDismiss,
}: {
  request: PurchaseConfirmationRequest;
  onDismiss: () => void;
}) {
  const router = useRouter();
  const reducedMotion = usePrefersReducedMotion();
  const view = useMemo(
    () => resolvePurchaseConfirmation(request),
    [request],
  );

  // Fire "shown" exactly once per confirmation (id changes → new confirmation).
  useEffect(() => {
    trackPurchaseConfirmationShown({
      scenario: view.scenario,
      kind: view.kind,
      plan: view.planId,
      monthlyCredits:
        request.kind === "plan" ? request.monthlyCredits ?? null : null,
      creditsAdded:
        request.kind === "credits" ? request.creditsAdded ?? null : null,
      priceUsd:
        request.kind === "plan" ? request.monthlyPriceUsd ?? null : null,
      source: view.source,
      projectId: view.projectId,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [request.id]);

  const handlePrimaryCta = () => {
    trackPurchaseConfirmationCtaClicked({
      scenario: view.scenario,
      kind: view.kind,
      plan: view.planId,
      ctaLabel: view.ctaLabel,
      source: view.source,
      projectId: view.projectId,
    });
    onDismiss();
    if (view.ctaHref) router.push(view.ctaHref);
  };

  const celebrate = view.celebrate && !reducedMotion;

  return (
    <Dialog open onOpenChange={(open) => !open && onDismiss()}>
      <DialogContent
        showCloseButton={false}
        // Override the compact default: no padding (sections manage their own),
        // a slightly wider card, and clipped corners so the header gradient and
        // confetti stay inside the rounded shape.
        className="max-w-[calc(100%-2rem)] gap-0 overflow-hidden p-0 sm:max-w-md"
      >
        {/* Confetti rains over the whole card (clipped to the rounded shape by
            the popup's overflow-hidden) and fades out. pointer-events-none so it
            never intercepts the close button or CTA. */}
        {celebrate && <Confetti />}

        {/* Celebratory header — a success-green gradient (the app's success
            colour, matching the benefit checkmarks) so the moment reads as a
            win, not a warning. White foreground for contrast in light + dark. */}
        <div className="relative isolate bg-linear-to-br from-emerald-500 via-emerald-600 to-green-700 px-6 pb-6 pt-8 text-center text-white">

          <button
            type="button"
            onClick={onDismiss}
            aria-label="Close"
            className="absolute right-3 top-3 z-20 inline-flex h-8 w-8 items-center justify-center rounded-full text-white/80 transition-colors hover:bg-white/15 hover:text-white focus:outline-none focus-visible:ring-2 focus-visible:ring-white/60"
          >
            <X className="h-4 w-4" />
          </button>

          <div className="relative z-20 flex flex-col items-center">
            <div
              className={`flex h-14 w-14 items-center justify-center rounded-full bg-white/20 ring-1 ring-white/40 ${
                reducedMotion ? "" : "animate-confirmation-pop"
              }`}
            >
              <Check className="h-7 w-7" strokeWidth={2.75} />
            </div>

            <DialogTitle className="mt-4 text-lg font-semibold text-white">
              {view.title}
            </DialogTitle>
            <DialogDescription className="mt-1 text-sm text-white/85">
              {view.subtitle}
            </DialogDescription>
          </div>
        </div>

        {/* Body — theme-aware via popover tokens. */}
        <div className="bg-popover px-6 py-5 text-popover-foreground">
          <div className="text-center">
            <p className="font-heading text-2xl font-semibold tracking-tight">
              {view.headline}
            </p>
            {view.priceNote && (
              <p className="mt-1 text-sm text-muted-foreground">
                {view.priceNote}
              </p>
            )}
          </div>

          <div className="mt-5">
            <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
              {view.benefitsLabel}
            </p>
            <ul className="mt-3 space-y-2.5">
              {view.benefits.map((benefit, i) => (
                <li key={i} className="flex items-start gap-2.5 text-sm">
                  <span className="mt-0.5 flex h-4 w-4 shrink-0 items-center justify-center rounded-full bg-emerald-500/15 text-emerald-600 dark:text-emerald-400">
                    <Check className="h-3 w-3" strokeWidth={3} />
                  </span>
                  <span className="text-foreground/90">{benefit}</span>
                </li>
              ))}
            </ul>
          </div>

          <button
            type="button"
            onClick={handlePrimaryCta}
            className="mt-6 flex h-11 w-full items-center justify-center rounded-full bg-linear-to-r from-emerald-500 to-green-600 text-base font-medium text-white shadow-md shadow-emerald-500/25 transition-opacity hover:opacity-90 focus:outline-none focus-visible:ring-2 focus-visible:ring-emerald-500/40"
          >
            {view.ctaLabel}
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
